import { useHttpClient } from '@wix/fe-essentials-standalone';
import {
  CREATE_CONTACT,
  GET_INITIALIZE_DATA,
  ADD_COMMENT,
  DELETE_COMMENT,
  UPDATE_COMMENT,
  CLIENT_REPLY_TO_COMMENT,
  METASITE_APP_ID,
} from './constants';
import { ACTIONS, useAppContext } from '@src/components/Contexts/AppContext';
import { pollRequest } from './fetchUtils';

export default (config: {
  feedbackId: string;
  metaSiteId: string;
  pMetaSiteId: string;
  siteId: string;
  editorType: string;
  siteName: string;
  ownerId: string;
  feedbackCreatorUserGuid: string;
  linkKey: string;
  instance?: string | null;
}) => {
  const {
    pMetaSiteId,
    feedbackId,
    metaSiteId,
    siteId,
    editorType,
    siteName,
    ownerId,
    feedbackCreatorUserGuid,
    linkKey,
  } = config;
  const httpClient = useHttpClient();
  const {
    dispatch,
    state: { instance, siteUrl },
  } = useAppContext();

  const getHeaders = (_instance?: string | null) => {
    return {
      headers: {
        Authorization: _instance ?? instance,
      },
    };
  };
  const createContact = async ({
    userName,
    userEmail,
  }: {
    userName: string;
    userEmail?: string;
  }) => {
    const [first, last] = userName.split(' ');
    const { data } = await httpClient.post<{ contact: { contactId: string } }>(
      `${CREATE_CONTACT}`,
      {
        first,
        last,
        email: userEmail,
        linkKey,
      },
      getHeaders(),
    );
    return data;
  };

  const getInitializeData = async () => {
    const siteCleanUrl = siteUrl.split('?')[0];
    async function fetchInstance() {
      const { data } = await httpClient.get<{
        apps: {
          [METASITE_APP_ID]: { instance: string };
        };
      }>(`${siteCleanUrl}/_api/v1/access-tokens`);
      if (data) {
        const _instance = data.apps?.[METASITE_APP_ID]?.instance;
        dispatch({
          type: ACTIONS.SET_INSTANCE,
          payload: { instance: _instance },
        });
        return _instance ?? null;
      }
      return null;
    }
    const _instance = await pollRequest(fetchInstance, 5, 500);
    const { data } = await httpClient.get<{
      comments: any[];
      userInfo: { userName: string; contactId: string; isAnonymous?: boolean };
      visitorId: string;
    }>(
      `${GET_INITIALIZE_DATA}?metaSiteId=${metaSiteId}&pMetaSiteId=${pMetaSiteId}`,
      getHeaders(_instance),
    );

    return data;
  };

  const addComment = async ({
    message,
    commentLocation,
    status,
    shouldSchedule,
    userInfo,
  }: {
    message: string;
    commentLocation: any;
    status: string;
    shouldSchedule: boolean;
    userInfo: any;
  }) => {
    const { data } = await httpClient.post<{ comment: any }>(
      `${ADD_COMMENT}`,
      {
        feedbackId,
        metaSiteId,
        pMetaSiteId,
        siteId,
        editorType,
        siteName,
        ownerId,
        feedbackCreatorUserGuid,
        ...(shouldSchedule && { shouldSchedule }),
        comment: {
          message,
          commentLocation,
          status,
          userInfo,
        },
      },
      getHeaders(),
    );
    return data.comment;
  };

  const deleteComment = async (commentId: string) => {
    const { data } = await httpClient.post(
      `${DELETE_COMMENT}`,
      {
        feedbackId,
        metaSiteId,
        commentId,
      },
      getHeaders(),
    );
    return data;
  };

  const updateComment = async ({
    id,
    message,
    commentLocation,
    commenterId,
    status,
    revision,
    fields,
  }: {
    id: string;
    feedbackId: string;
    message: string;
    commentLocation: any;
    commenterId: string;
    status: string;
    revision: string;
    fields: any;
  }) => {
    const { data } = await httpClient.post<{ comment: any }>(
      `${UPDATE_COMMENT}`,
      {
        feedbackId,
        commentId: id,
        metaSiteId,
        commenterId,
        comment: {
          message,
          commentLocation,
          status,
          revision,
        },
        fields,
      },
      getHeaders(),
    );
    return data.comment;
  };

  const replyToComment = async ({
    id,
    revision,
    replyText,
    userInfo,
    commenterId,
    isOwner,
  }: {
    id: string;
    revision: string;
    replyText: string;
    userInfo: any;
    commenterId: string;
    isOwner: boolean;
  }) => {
    const { data } = await httpClient.post<{ comment: any }>(
      CLIENT_REPLY_TO_COMMENT,
      {
        feedbackId,
        commentId: id,
        metaSiteId,
        revision,
        isOwner,
        siteName,
        ownerId,
        feedbackCreatorUserGuid,
        editorType,
        siteId,
        commenterInfo: userInfo,
        commenterId,
        reply: {
          message: replyText,
        },
        pMetaSiteId,
      },
      getHeaders(),
    );

    return data.comment;
  };

  return {
    addComment,
    getInitializeData,
    createContact,
    deleteComment,
    updateComment,
    replyToComment,
  };
};
